import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useOrder, useOrderProgress } from "../context/OrderContext";
import { PENDING_ORDER_KEY } from "../context/OrderContext";
import "./OrderStatus.css";
import { API_URL } from "../config/constants";

// Robot-themed messages (pending = order in queue when kitchen is busy)
const robotMessages = {
  pending: [
    "🤖 Order placed! You're in the queue.",
    "We'll start preparing as soon as the current order is done.",
    "Scanning recipe database..."
  ],
  processing: [
    "🦾 Robotic arm activated!",
    "Calibrating espresso parameters...",
    "Loading ingredients into system..."
  ],
  cooking: [
    "☕ AutoChef is brewing your drink!",
    "Precision temperature: 93°C achieved",
    "Extracting perfect espresso shot..."
  ],
  failed: [
    "⚠️ Something went wrong with your order.",
    "Please see the counter for assistance.",
    "Our team will help you right away."
  ],
  ready: [
    "✨ Your drink is ready!",
    "Robot barista completed the task",
    "Please collect from the counter"
  ],
  completed: [
    "🎉 Order delivered successfully!",
    "Thank you for choosing AutoChef",
    "Robot barista awaits your next order"
  ],
  cancelled: [
    "Order cancelled",
    "This order was cancelled.",
    "Please place a new order if needed."
  ]
};

const OrderStatus = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { socket, setActiveOrder, setIsProcessing } = useOrder();
  const { actionProgress } = useOrderProgress();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageIndex, setMessageIndex] = useState(0);
  
  // Normalized status (backend may return "CANCELLED" or "cancelled")
  const status = (order?.order_status || "pending").toLowerCase();
  // Don't show cooking progress when order is cancelled
  const currentProgress = actionProgress && actionProgress.orderId === parseInt(orderId) && status !== "cancelled"
    ? actionProgress
    : null;

  // Rotate through messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchOrder = useCallback(async () => {
    try {
      const response = await axios.post(
        `${API_URL}/graphql`,
        {
          query: `
          query($orderId: Int!) {
            order(order_id: $orderId) {
              order_id
              order_number
              order_status
              total_amount
              items {
                item_id
                recipe_id
                quantity
                status
                recipe {
                  recipe_name
                  recipe_image
                }
              }
            }
          }
        `,
        variables: { orderId: parseInt(orderId, 10) },
        },
        { timeout: 12000 }
      );

      const o = response.data?.data?.order || response.data?.order;
      if (o) {
        // Normalize order_status (backend may return "CANCELLED" or "cancelled")
        const normalized = { ...o, order_status: (o.order_status || "pending").toLowerCase() };
        setOrder(normalized);
        setActiveOrder(normalized);
        const status = normalized.order_status;
        setIsProcessing(
          ["pending", "processing", "cooking"].includes(status)
        );
        if (["ready", "completed", "cancelled"].includes(status)) {
          sessionStorage.removeItem(PENDING_ORDER_KEY);
        }
      }
    } catch (error) {
      // Network error - user can retry by refreshing
    } finally {
      setLoading(false);
    }
  }, [orderId, setActiveOrder, setIsProcessing]);

  useEffect(() => {
    let isMounted = true;
    let timeoutId;
    let pollInterval;

    fetchOrder();

    // Poll every 15s as fallback if socket misses orderItemStatusUpdate (reduces backend load)
    pollInterval = setInterval(() => {
      if (isMounted) fetchOrder();
    }, 15000);

    if (socket) {
      socket.emit("subscribeToOrder", { orderId: parseInt(orderId) });

      const handleOrderStatusUpdate = (data) => {
        if (!isMounted) return;
        if (data.orderId === parseInt(orderId)) {
          const status = (data.status || "").toLowerCase();
          setOrder((prev) => ({ ...prev, order_status: status }));

          if (["ready", "completed", "cancelled"].includes(status)) {
            sessionStorage.removeItem(PENDING_ORDER_KEY);
            setIsProcessing(false);
          }
        }
      };

      const handleOrderItemStatusUpdate = (data) => {
        if (!isMounted) return;
        if (data.orderId === parseInt(orderId)) {
          setOrder((prev) => {
            if (!prev || !prev.items) return prev;
            const updatedItems = prev.items.map((item) =>
              item.item_id === data.itemId
                ? { ...item, status: data.status }
                : item
            );
            return { ...prev, items: updatedItems };
          });
        }
      };

      socket.on("orderStatusUpdate", handleOrderStatusUpdate);
      socket.on("orderItemStatusUpdate", handleOrderItemStatusUpdate);

      return () => {
        isMounted = false;
        clearTimeout(timeoutId);
        clearInterval(pollInterval);
        if (socket) {
          socket.off("orderStatusUpdate", handleOrderStatusUpdate);
          socket.off("orderItemStatusUpdate", handleOrderItemStatusUpdate);
        }
      };
    }

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      clearInterval(pollInterval);
    };
  }, [orderId, socket, navigate, setIsProcessing, fetchOrder]);

  if (loading) {
    return (
      <div className="order-status-page">
        <div className="loading-state">
          <div className="robot-icon">🤖</div>
          <p>Connecting to AutoChef...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-status-page">
        <div className="error-state">
          <div className="robot-icon">🤖</div>
          <p>Order not found</p>
          <button onClick={() => navigate("/")}>Back to Menu</button>
        </div>
      </div>
    );
  }

  const messages = robotMessages[status] || robotMessages.pending;
  const currentMessage = messages[messageIndex];

  const getStatusIcon = () => {
    switch (status) {
      case "pending": return "🤖";
      case "processing": return "🦾";
      case "cooking": return "☕";
      case "failed": return "⚠️";
      case "ready": return "✨";
      case "completed": return "🎉";
      case "cancelled": return "🚫";
      default: return "🤖";
    }
  };

  const getProgressPercent = () => {
    switch (status) {
      case "pending": return 20;
      case "processing": return 40;
      case "cooking": return 70;
      case "ready": return 100;
      case "completed": return 100;
      case "cancelled": return 0;
      default: return 10;
    }
  };

  return (
    <div className="order-status-page">
      <div className="status-card">
        {/* Header */}
        <div className="status-header">
          <div className="robot-animation">
            <span className="status-icon">{getStatusIcon()}</span>
          </div>
          <h1 className="status-title">{currentMessage}</h1>
          <p className="order-num">Order #{order.order_number?.slice(-8)}</p>
        </div>

        {/* Progress Bar */}
        <div className="progress-section">
          {currentProgress ? (
            <>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${currentProgress.progressPercent || 0}%` }}
                />
              </div>
              <div className="progress-info">
                <span className="progress-message">{currentProgress.message}</span>
                {currentProgress.progressPercent !== null && (
                  <span className="progress-percent">{currentProgress.progressPercent}%</span>
                )}
              </div>
              {currentProgress.totalSteps && (
                <div className="progress-steps">
                  Step {currentProgress.stepIndex + 1} of {currentProgress.totalSteps}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${getProgressPercent()}%` }}
                />
              </div>
              <div className="progress-steps">
                <span className={status !== "pending" ? "done" : "active"}>Received</span>
                <span className={["cooking", "ready", "completed"].includes(status) ? "done" : status === "processing" ? "active" : ""}>Preparing</span>
                <span className={["ready", "completed"].includes(status) ? "done" : status === "cooking" ? "active" : ""}>Brewing</span>
                <span className={status === "completed" ? "done" : status === "ready" ? "active" : ""}>Ready</span>
              </div>
            </>
          )}
        </div>

        {/* Items */}
        <div className="items-section">
          {order.items?.map((item) => (
            <div key={item.item_id} className={`status-item ${item.status}`}>
              <div className="item-thumb">
                <img
                  src={item.recipe?.recipe_image || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect fill='%23ddd' width='60' height='60'/%3E%3C/svg%3E"}
                  alt={item.recipe?.recipe_name}
                  onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect fill='%23ddd' width='60' height='60'/%3E%3C/svg%3E"; }}
                />
              </div>
              <div className="item-info">
                <span className="item-name">{item.recipe?.recipe_name}</span>
                <span className="item-qty">×{item.quantity}</span>
              </div>
              <div className={`item-badge ${item.status}`}>
                {item.status === "pending" && "⏳"}
                {item.status === "cooking" && "🔥"}
                {item.status === "failed" && "❌"}
                {item.status === "cancelled" && "🚫"}
                {item.status === "ready" && "✅"}
                {item.status === "completed" && "🎉"}
              </div>
            </div>
          ))}
        </div>

        {/* Message */}
        <div className="robot-message">
          {status === "failed" ? (
            <p>Please see the counter for assistance.</p>
          ) : status === "cancelled" ? (
            <p>This order was cancelled. Please place a new order if needed.</p>
          ) : ["ready", "completed"].includes(status) ? (
            <p className="ready-message">Your order is ready. Please collect from the counter.</p>
          ) : (
            <>
              <span className="typing-indicator">
                <span></span><span></span><span></span>
              </span>
              <p>AutoChef is preparing your order...</p>
            </>
          )}
        </div>

        {/* Action */}
        <button className="back-btn" onClick={() => navigate("/")}>
          ← Back to Menu
        </button>
      </div>
    </div>
  );
};

export default OrderStatus;
